import { Navigate, useLocation } from "react-router-dom";
import { isUserLoggedIn } from "./utils/helpers";
import { rememberPostAuthReturnPath } from "./utils/sharedCloneReturn";
import BrandWheelLoader from "./components/BrandWheelLoader";

/**
 * stgfix-1008-P0-6601 (reworked from the prior `rememberReturnOnRedirect`
 * prop): a logged-out visitor redirected to /login from one of these routes
 * ALSO gets the current path+search durably remembered (localStorage, see
 * sharedCloneReturn.js), so it survives a brand-new visitor choosing Signup
 * instead of Login (react-router's own `state.from` does not — Signup's
 * success path never reads router state). Listed here instead of as a
 * per-call JSX prop so every `<PrivateRoute>` call site in routes.js stays
 * byte-identical to Usman's 2026-09-28 login-guard wiring (fa7371c3a) —
 * stg-clobber-gate does a literal line diff and a changed opening tag reads
 * as "removed the guard", even though the guard itself never moved.
 */
const ROUTES_REMEMBER_RETURN_ON_REDIRECT = [
  "/invite/accept",
  "/collaboration/accept",
  "/collaboration/join",
];

function PrivateRoute({ children }) {
  const location = useLocation();

  if (!isUserLoggedIn()) {
    if (ROUTES_REMEMBER_RETURN_ON_REDIRECT.includes(location.pathname)) {
      rememberPostAuthReturnPath(`${location.pathname}${location.search}`);
    }
    // stgfix-1008-NEW-login-1 (webkit blank-gray-before-login, MEASURED live
    // web-stg.iyuga.net): `<Navigate>` renders null and only performs the
    // actual redirect in a useEffect, so without this a logged-out visitor's
    // FIRST React commit here paints nothing. src/index.js's boot-loader
    // teardown is built on "the first commit always paints the BrandWheelLoader
    // Suspense fallback" (every route page is React.lazy) — a PrivateRoute
    // redirect broke that invariant, so the boot loader tore down with nothing
    // live to hand off to: a real blank-gray gap until the SECOND commit
    // (post-redirect /login) lands. Rendering the identical fullpage loader
    // (see routes.js's shared Suspense fallback) in this SAME commit restores
    // the invariant for every PrivateRoute-guarded entry point, not just
    // Collaboration/Team invite links. `skipEntry` because this is a true
    // continuation of an already-on-screen loader (the boot loader), exactly
    // the handoff case the prop exists for.
    return (
      <>
        <BrandWheelLoader variant="fullpage" label="Loading iYuga…" disc={false} skipEntry />
        <Navigate to="/login" state={{ from: location }} replace />
      </>
    );
  }

  return children;
}
export { PrivateRoute };
